import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation';
import HotelBookingForm from '../components/HotelBookingForm';
import HotelBookingsDisplay from '../components/HotelBookingsDisplay';
import HotelBookingCards from '../components/HotelBookingCards';
import HotelGroupVetForm from '../components/HotelGroupVetForm';
import ConfirmDialog from '../components/ConfirmDialog';
import { useHotelBookings } from '../hooks/useHotelBookings';
import '../styles/pages/hotel-new.css';
import '../styles/components/dog-form-enhancements.css';

const Hotel = () => {
    // Declared before the hook: only the selected year is fetched from the API.
    const [yearFilter, setYearFilter] = useState(new Date().getFullYear()); // Current year

    const {
        bookings,
        loading,
        error,
        bookingStats,
        createSimpleBooking,
        updateBooking,
        checkInExistingBooking,
        checkOutGuest,
        updateReservationBookings,
        deleteBooking,
        clearError
    } = useHotelBookings(yearFilter);

    const location = useLocation();
    const [activeTab, setActiveTab] = useState(() => location.state?.openTab || 'overview');
    const [editingBooking, setEditingBooking] = useState(null);
    // The bookings of a reservation being checked in / out together
    const [reservationGroup, setReservationGroup] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [monthFilter, setMonthFilter] = useState(new Date().getMonth() + 1); // Current month (1-12)
    const [formType, setFormType] = useState('booking'); // 'booking', 'checkin', 'checkout'
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null); // function to run if the user confirms discarding changes

    // Run `action` immediately, unless the booking form has unsaved changes —
    // in that case, hold it until the user confirms discarding them.
    const guardNavigation = (action) => {
        if (activeTab === 'booking-form' && isFormDirty) {
            setPendingNavigation(() => action);
        } else {
            action();
        }
    };

    const confirmDiscardChanges = () => {
        const action = pendingNavigation;
        setPendingNavigation(null);
        setIsFormDirty(false);
        if (action) action();
    };

    const cancelDiscardChanges = () => setPendingNavigation(null);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => {
                setMessage({ type: '', text: '' });
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    // Handle form submission based on type
    const handleFormSubmit = async (formData) => {
        try {
            clearError();
            let success = false;
            
            if (formType === 'booking') {
                if (editingBooking) {
                    const updateData = {
                        dogId: formData.dogId,
                        checkIn: formData.checkIn,
                        checkOut: formData.checkOut,
                        notes: formData.notes?.trim() || ''
                    };
                    success = await updateBooking(editingBooking._id, updateData);
                    if (success) {
                        setMessage({ type: 'success', text: '¡Reserva de hotel actualizada con éxito!' });
                    }
                } else {
                    // Multi-dog: create one booking per selected dog
                    const dogIds = formData.dogIds || [];
                    const vetDataByDog = formData.vetDataByDog || {};
                    // Links the dogs so they share one card, check-in and intake form
                    const reservationId = dogIds.length > 1 ? crypto.randomUUID() : undefined;
                    const results = await Promise.all(
                        dogIds.map(dogId => createSimpleBooking({
                            dogId,
                            reservationId,
                            checkIn: formData.checkIn,
                            checkOut: formData.checkOut,
                            notes: formData.notes,
                            ...(vetDataByDog[dogId] ? { vetCheckIn: vetDataByDog[dogId] } : {})
                        }))
                    );
                    success = results.length > 0 && results.every(r => r);
                    if (success) {
                        const n = dogIds.length;
                        setMessage({ type: 'success', text: `¡${n} reserva${n > 1 ? 's' : ''} creada${n > 1 ? 's' : ''} con éxito!` });
                    }
                }
            } else if (formType === 'checkin') {
                success = await checkInExistingBooking(formData);
                if (success) {
                    setMessage({ type: 'success', text: '¡Entrada del huésped registrada con éxito!' });
                }
            } else if (formType === 'editVetCheckin') {
                const existingVet = editingBooking.vetCheckIn || {};
                const vetCheckIn = {
                    ...existingVet, // preserve all existing fields as base
                    ...(formData.vetWeight && { weight: parseFloat(formData.vetWeight) }),
                    ...(formData.vetTemperature && { temperature: parseFloat(formData.vetTemperature) }),
                    ...(formData.vetNotes?.trim() && { notes: formData.vetNotes.trim() }),
                    ...(formData.vetCheckedBy?.trim() && { checkedBy: formData.vetCheckedBy.trim() }),
                    ...(formData.vetMedication?.trim() && { medication: formData.vetMedication.trim() }),
                    // A new signature wins; an explicit clear sends '' so the
                    // server erases it. Otherwise the key is left out entirely
                    // and the stored signature is carried over — `existingVet`
                    // only holds one if the card finished fetching it.
                    ...(formData.ownerSignature
                        ? { ownerSignature: formData.ownerSignature }
                        : formData.ownerSignatureCleared ? { ownerSignature: '' } : {}),
                };
                success = await updateBooking(editingBooking._id, { vetCheckIn });
                if (success) {
                    setMessage({ type: 'success', text: '¡Información de salud actualizada con éxito!' });
                }
            } else if (formType === 'checkout') {
                success = await checkOutGuest(formData);
                if (success) {
                    setMessage({ type: 'success', text: '¡Salida del huésped registrada con éxito!' });
                }
            } else if (formType === 'groupCheckin' || formType === 'groupCheckout') {
                const isCheckout = formType === 'groupCheckout';
                const vetKey = isCheckout ? 'vetCheckOut' : 'vetCheckIn';
                const updates = formData.entries.map(entry => {
                    const vet = {
                        ...(entry.vetWeight && { weight: parseFloat(entry.vetWeight) }),
                        ...(entry.vetTemperature && { temperature: parseFloat(entry.vetTemperature) }),
                        ...(entry.vetNotes?.trim() && { notes: entry.vetNotes.trim() }),
                        ...(entry.vetMedication?.trim() && { medication: entry.vetMedication.trim() }),
                        ...(formData.vetCheckedBy?.trim() && { checkedBy: formData.vetCheckedBy.trim() }),
                        // The one signature covers every dog in the reservation
                        ...(formData.ownerSignature && { ownerSignature: formData.ownerSignature }),
                    };
                    return {
                        bookingId: entry.bookingId,
                        data: {
                            status: isCheckout ? 'checked-out' : 'checked-in',
                            ...(Object.keys(vet).length ? { [vetKey]: vet } : {})
                        }
                    };
                });

                const failed = await updateReservationBookings(updates);
                success = failed.length === 0;
                if (success) {
                    const n = updates.length;
                    setMessage({ type: 'success', text: `¡${isCheckout ? 'Salida' : 'Entrada'} registrada para ${n} perro${n !== 1 ? 's' : ''}!` });
                } else {
                    // Stay on the form: saving again re-sends the ones that went
                    // through, which is harmless, and retries the rest.
                    const names = reservationGroup
                        .filter(b => failed.includes(b._id))
                        .map(b => b.dog?.name || 'Perro desconocido')
                        .join(', ');
                    setMessage({ type: 'error', text: `No se pudo registrar la ${isCheckout ? 'salida' : 'entrada'} de: ${names}. Los demás sí se guardaron — intenta de nuevo.` });
                }
            }

            if (success) {
                setIsFormDirty(false);
                setEditingBooking(null);
                setReservationGroup(null);
                setActiveTab('overview');
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message || 'Operación fallida' });
        }
    };

    // Handle edit booking
    const handleEdit = (booking) => guardNavigation(() => {
        setEditingBooking(booking);
        setFormType('booking');
        setActiveTab('booking-form');
    });

    // Handle check-in action. A reservation card passes all its dogs at once.
    const handleCheckIn = (booking) => guardNavigation(() => {
        if (Array.isArray(booking)) {
            setReservationGroup(booking);
            setEditingBooking(null);
            setFormType('groupCheckin');
        } else {
            setEditingBooking(booking);
            setFormType('checkin');
        }
        setActiveTab('booking-form');
    });

    // Handle check-out action. A reservation card passes all its dogs at once.
    const handleCheckOut = (booking) => guardNavigation(() => {
        if (Array.isArray(booking)) {
            setReservationGroup(booking);
            setEditingBooking(null);
            setFormType('groupCheckout');
        } else {
            setEditingBooking(booking);
            setFormType('checkout');
        }
        setActiveTab('booking-form');
    });

    // Handle edit vet check-in health info on an already checked-in booking
    const handleEditVetCheckin = (booking) => guardNavigation(() => {
        setEditingBooking(booking);
        setFormType('editVetCheckin');
        setActiveTab('booking-form');
    });

    // Handle delete booking
    const handleDelete = async (bookingId) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar esta reserva de hotel?')) {
            try {
                clearError();
                await deleteBooking(bookingId);
                setMessage({ type: 'success', text: '¡Reserva de hotel eliminada con éxito!' });
            } catch (err) {
                setMessage({ type: 'error', text: err.message || 'No se pudo eliminar la reserva' });
            }
        }
    };

    // Handle form cancel
    const handleFormCancel = () => {
        setEditingBooking(null);
        setReservationGroup(null);
        setFormType('booking');
        setActiveTab('overview');
    };

    // Filter bookings for the all bookings tab (search + status + month + year)
    const getFilteredBookings = () => {
        return bookings.filter(booking => {
            const matchesSearch = !searchTerm ||
                booking.dog?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                booking.dog?.owners?.some(owner =>
                    owner.name.toLowerCase().includes(searchTerm.toLowerCase())
                );

            const matchesStatus = !statusFilter || booking.status === statusFilter;

            const checkInDate = new Date(booking.checkIn);
            const matchesMonth = (checkInDate.getMonth() + 1) === monthFilter;
            const matchesYear = checkInDate.getFullYear() === yearFilter;

            return matchesSearch && matchesStatus && matchesMonth && matchesYear;
        });
    };

    // Group the (already month/year-filtered) bookings by month for display
    const getBookingsGroupedByMonth = () => {
        const monthBookings = getFilteredBookings()
            .sort((a, b) => new Date(b.checkIn) - new Date(a.checkIn));

        const grouped = {};
        if (monthBookings.length > 0) {
            grouped[monthFilter] = monthBookings;
        }
        return grouped;
    };

    return (
        <div>
            <Navigation />
            <div className="hotel-container">
                {/* Tab Navigation — before header so it's visible immediately on mobile */}
                <div className="hotel-tab-nav">
                    <button
                        onClick={() => guardNavigation(() => {
                            setActiveTab('overview');
                            setEditingBooking(null);
                            setReservationGroup(null);
                            setFormType('booking');
                        })}
                        className={`hotel-tab-btn${activeTab === 'overview' ? ' active' : ''}`}
                    >
                        📋 Resumen
                    </button>
                    <button
                        onClick={() => guardNavigation(() => {
                            setActiveTab('booking-form');
                            setFormType('booking');
                            if (!editingBooking) setEditingBooking(null);
                        })}
                        className={`hotel-tab-btn${activeTab === 'booking-form' && formType === 'booking' ? ' active' : ''}`}
                    >
                        ➕ Nueva reserva
                    </button>
                    <button
                        onClick={() => guardNavigation(() => {
                            setActiveTab('booking-form');
                            setFormType('checkin');
                            setEditingBooking(null);
                        })}
                        className={`hotel-tab-btn${activeTab === 'booking-form' && formType === 'checkin' ? ' active' : ''}`}
                    >
                        🛎️ Entrada
                    </button>
                    <button
                        onClick={() => guardNavigation(() => {
                            setActiveTab('booking-form');
                            setFormType('checkout');
                            setEditingBooking(null);
                        })}
                        className={`hotel-tab-btn${activeTab === 'booking-form' && formType === 'checkout' ? ' active' : ''}`}
                    >
                        🧳 Salida
                    </button>
                    <button
                        onClick={() => guardNavigation(() => {
                            setActiveTab('all-bookings');
                            setEditingBooking(null);
                        })}
                        className={`hotel-tab-btn${activeTab === 'all-bookings' ? ' active' : ''}`}
                    >
                        📅 Reservas
                    </button>
                </div>

                <div className="hotel-header">
                    <h1>🏨 Gestión de Hotel</h1>
                    <p className="page-subtitle">Administra reservas de hotel y servicios para huéspedes</p>
                </div>

                {/* Messages */}
                {(message.text || error) && (
                    <div className={`message ${message.type || 'error'}`}>
                        {message.text || error}
                    </div>
                )}

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="overview-tab">
                        <div className="quick-stats">
                            <div className="stat-card">
                                <h3>🏨 Huéspedes actuales</h3>
                                <p className="stat-number">{bookingStats.current}</p>
                                <p className="stat-label">con entrada</p>
                            </div>
                            <div className="stat-card">
                                <h3>📋 Próximas</h3>
                                <p className="stat-number">{bookingStats.upcoming}</p>
                                <p className="stat-label">reserva{bookingStats.upcoming !== 1 ? 's' : ''}</p>
                            </div>
                            <div className="stat-card">
                                <h3>📊 Total</h3>
                                <p className="stat-number">{bookingStats.total}</p>
                                <p className="stat-label">reserva{bookingStats.total !== 1 ? 's' : ''}</p>
                            </div>
                        </div>

                        <HotelBookingsDisplay
                            bookings={bookings}
                            loading={loading}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onCheckIn={handleCheckIn}
                            onCheckOut={handleCheckOut}
                            onEditVetCheckin={handleEditVetCheckin}
                        />
                    </div>
                )}

                {/* Booking Form Tab */}
                {activeTab === 'booking-form' && (
                    <div className="booking-form-tab">
                        {reservationGroup && (formType === 'groupCheckin' || formType === 'groupCheckout') ? (
                            <HotelGroupVetForm
                                key={`${formType}-${reservationGroup[0]._id}`}
                                mode={formType === 'groupCheckout' ? 'checkout' : 'checkin'}
                                bookings={reservationGroup}
                                onSubmit={handleFormSubmit}
                                onCancel={() => guardNavigation(handleFormCancel)}
                                onDirtyChange={setIsFormDirty}
                                loading={loading}
                            />
                        ) : (
                            <HotelBookingForm
                                key={`${formType}-${editingBooking?._id || 'new'}`}
                                onSubmit={handleFormSubmit}
                                onCancel={() => guardNavigation(handleFormCancel)}
                                onDirtyChange={setIsFormDirty}
                                loading={loading}
                                formType={formType}
                                editingBooking={editingBooking}
                                bookings={bookings}
                            />
                        )}
                    </div>
                )}

                {/* All Bookings Tab */}
                {activeTab === 'all-bookings' && (
                    <div className="all-bookings-tab">
                        <div className="bookings-header">
                            <h2>Todas las reservas de hotel</h2>

                            {/* Filters */}
                            <div className="bookings-filters">
                                <div className="filter-group">
                                    <label>Buscar</label>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Buscar por nombre del perro o propietario..."
                                    />
                                </div>
                                <div className="filter-group">
                                    <label>Mes</label>
                                    <select
                                        value={monthFilter}
                                        onChange={(e) => setMonthFilter(parseInt(e.target.value))}
                                    >
                                        <option value={1}>Enero</option>
                                        <option value={2}>Febrero</option>
                                        <option value={3}>Marzo</option>
                                        <option value={4}>Abril</option>
                                        <option value={5}>Mayo</option>
                                        <option value={6}>Junio</option>
                                        <option value={7}>Julio</option>
                                        <option value={8}>Agosto</option>
                                        <option value={9}>Septiembre</option>
                                        <option value={10}>Octubre</option>
                                        <option value={11}>Noviembre</option>
                                        <option value={12}>Diciembre</option>
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Año</label>
                                    <select
                                        value={yearFilter}
                                        onChange={(e) => setYearFilter(parseInt(e.target.value))}
                                    >
                                        {(() => {
                                            const currentYear = new Date().getFullYear();
                                            const years = [];
                                            // Show 2 years back and 3 years forward
                                            for (let year = currentYear - 2; year <= currentYear + 3; year++) {
                                                years.push(
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                );
                                            }
                                            return years;
                                        })()} 
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Estado</label>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="">Todos los estados</option>
                                        <option value="booked">Reservado</option>
                                        <option value="checked-in">Con entrada</option>
                                        <option value="checked-out">Con salida</option>
                                    </select>
                                </div>
                                {(searchTerm || statusFilter || monthFilter !== new Date().getMonth() + 1 || yearFilter !== new Date().getFullYear()) && (
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setStatusFilter('');
                                            setMonthFilter(new Date().getMonth() + 1);
                                            setYearFilter(new Date().getFullYear());
                                        }}
                                        className="clear-filters-btn"
                                    >
                                        Limpiar filtros
                                    </button>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading">Cargando todas las reservas de hotel...</div>
                        ) : (() => {
                            const groupedBookings = getBookingsGroupedByMonth();
                            const hasBookings = Object.keys(groupedBookings).length > 0;

                            if (!hasBookings) {
                                const monthYearLabel = new Date(yearFilter, monthFilter - 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
                                return (
                                    <div className="no-bookings">
                                        {searchTerm || statusFilter ? (
                                            <p>Ninguna reserva coincide con tu búsqueda para {monthYearLabel}.</p>
                                        ) : (
                                            <p>No se encontraron reservas de hotel para {monthYearLabel}.</p>
                                        )}
                                    </div>
                                );
                            }

                            const monthNames = [
                                'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                            ];
                            
                            return (
                                <div className="bookings-grouped-display">
                                    {/* Year Header */}
                                    <div className="year-header">
                                        <h2>📅 {yearFilter}</h2>
                                    </div>
                                    
                                    {/* Month Sections */}
                                    {Object.keys(groupedBookings).map(month => {
                                        const monthNum = parseInt(month);
                                        const monthName = monthNames[monthNum - 1];
                                        const monthBookings = groupedBookings[month];
                                        
                                        return (
                                            <div key={month} className="month-section">
                                                <div className="month-header">
                                                    <h3>{monthName}</h3>
                                                    <span className="bookings-count">{monthBookings.length} reserva{monthBookings.length !== 1 ? 's' : ''}</span>
                                                </div>
                                                
                                                <div className="bookings-grid">
                                                    <HotelBookingCards
                                                        bookings={monthBookings}
                                                        onEdit={handleEdit}
                                                        onDelete={handleDelete}
                                                        onCheckIn={handleCheckIn}
                                                        onCheckOut={handleCheckOut}
                                                        onEditVetCheckin={handleEditVetCheckin}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>

            <ConfirmDialog
                isOpen={pendingNavigation !== null}
                icon="🐾"
                title="¡Espera! Tienes cambios sin guardar"
                message="Si sales ahora, se perderán los cambios que hiciste en este formulario."
                confirmLabel="Salir sin guardar"
                cancelLabel="Seguir editando"
                danger
                onConfirm={confirmDiscardChanges}
                onCancel={cancelDiscardChanges}
            />
        </div>
    );
};

export default Hotel;